import * as v from 'valibot'
import {
  POLICY_INDICATION_EDIT_TYPE,
  type PolicyIndicationEditType,
} from '../dde'
import { POLICY_SYNC_FORMAT_VERSION } from '../finalize/types'
import { drugSchema, indicationSchema } from '../types'
import {
  canonicalClone,
  compareCodeUnits,
  hashCanonicalValue,
} from './canonical'
import {
  ConfigurationDomainError,
  type StandardRevision,
  type StandardRevisionIdentity,
  type StandardRevisionIssue,
  type StandardSourceVersion,
} from './types'

const supportedTypes = new Set<PolicyIndicationEditType>(
  Object.values(POLICY_INDICATION_EDIT_TYPE),
)

const policySyncInputSchema = v.object({
  formatVersion: v.number(),
  revision: v.number(),
  drug: drugSchema,
  completedIndications: v.array(indicationSchema),
  applicableTypes: v.array(
    v.picklist([
      'Age',
      'Diagnosis',
      'Frequency',
      'MaxDailyUnits',
      'MaxUnitsOverTime',
    ]),
  ),
  documents: v.record(
    v.string(),
    v.object({
      id: v.string(),
      name: v.string(),
      hash: v.optional(v.string()),
      metadata: v.optional(
        v.object({
          citation: v.optional(v.string()),
          effectiveTime: v.optional(v.string()),
        }),
      ),
    }),
  ),
})

type ValidatedPolicySyncInput = v.InferOutput<typeof policySyncInputSchema>

const REVISION_ISSUES_KEY = 'revisionIssues'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const indicationRef = (indication: unknown) => {
  const record = isRecord(indication) ? indication : {}
  const id = text(record.id)
  const name = text(record.name)
  return {
    ...(id && { indicationId: id }),
    ...(name && { indicationName: name }),
  }
}

/**
 * Checks each indication's identity and source provenance. Reads the raw
 * snapshot so these still report alongside schema issues when parsing fails.
 */
const indicationIssues = (snapshot: object): StandardRevisionIssue[] => {
  const { completedIndications, documents } = snapshot as Record<
    string,
    unknown
  >
  const indications = Array.isArray(completedIndications)
    ? completedIndications
    : []
  const sources = isRecord(documents) ? documents : {}
  const seen = new Set<string>()

  return indications.flatMap((indication): StandardRevisionIssue[] => {
    const record = isRecord(indication) ? indication : {}
    const ref = indicationRef(indication)
    const id = text(record.id)
    const issues: StandardRevisionIssue[] = []

    if (id && seen.has(id)) {
      issues.push({
        kind: 'duplicate',
        scope: 'indication',
        field: 'id',
        message: `Standard Revision contains duplicate indication ${id}`,
        ...ref,
      })
    }
    seen.add(id)

    for (const field of ['id', 'name'] as const) {
      if (!text(record[field])) {
        issues.push({
          kind: 'missing',
          scope: 'indication',
          field,
          message: `Standard Revision indication ${id || '<unknown>'} is incomplete`,
          ...ref,
        })
      }
    }

    const documentId = text(record.sourceDocumentId)
    const document = documentId ? sources[documentId] : undefined
    if (
      !isRecord(document) ||
      document.id !== documentId ||
      !text(document.hash)
    ) {
      issues.push({
        kind: 'unversioned_source',
        scope: 'indication',
        field: 'sourceDocumentId',
        message: `Standard Revision indication ${id || '<unknown>'} lacks a versioned source document`,
        ...ref,
      })
    }
    return issues
  })
}

const schemaIssue = (
  issue: v.BaseIssue<unknown>,
  snapshot: object,
): StandardRevisionIssue => {
  const [head = '', ...rest] = (issue.path ?? []).map(({ key }) => String(key))
  const kind = issue.received === 'undefined' ? 'missing' : 'invalid'
  const { message } = issue
  const { completedIndications } = snapshot as Record<string, unknown>

  if (head === 'completedIndications' && rest.length > 1) {
    const indications = Array.isArray(completedIndications)
      ? completedIndications
      : []
    return {
      kind,
      scope: 'indication',
      field: rest.slice(1).join('.'),
      message,
      ...indicationRef(indications[Number(rest[0])]),
    }
  }
  if (head === 'drug' && rest.length > 0) {
    return { kind, scope: 'drug', field: rest.join('.'), message }
  }
  if (head === 'documents' && rest.length > 1) {
    return { kind, scope: 'document', field: rest.slice(1).join('.'), message }
  }
  return { kind, scope: 'snapshot', field: [head, ...rest].join('.'), message }
}

const uniqueIssues = (issues: StandardRevisionIssue[]) => [
  ...new Map(
    issues.map((issue) => [
      [issue.scope, issue.kind, issue.field, issue.indicationId].join('\u0000'),
      issue,
    ]),
  ).values(),
]

/** The located issues behind a failed adaptation, or `[]` when it failed for a non-content reason. */
export const standardRevisionIssuesOf = (
  error: ConfigurationDomainError,
): StandardRevisionIssue[] => {
  const issues = error.details?.[REVISION_ISSUES_KEY]
  return Array.isArray(issues) ? (issues as StandardRevisionIssue[]) : []
}

const sourceVersionsFrom = (
  input: ValidatedPolicySyncInput,
): StandardSourceVersion[] => {
  const sourceVersions = new Map<string, StandardSourceVersion>()
  for (const indication of input.completedIndications) {
    const documentId = indication.sourceDocumentId?.trim()
    const contentHash = documentId && input.documents[documentId]?.hash
    if (!documentId || !contentHash) continue
    sourceVersions.set(documentId, { documentId, contentHash })
  }

  return [...sourceVersions.values()].sort((left, right) =>
    compareCodeUnits(left.documentId, right.documentId),
  )
}

export const adaptPolicySyncInputToStandardRevision = async ({
  ref,
  snapshot,
}: {
  ref: StandardRevisionIdentity
  snapshot: unknown
}): Promise<StandardRevision> => {
  if (
    !snapshot ||
    typeof snapshot !== 'object' ||
    !('formatVersion' in snapshot)
  ) {
    throw new ConfigurationDomainError(
      'BASELINE_INCOMPLETE',
      'Standard Revision snapshot is invalid',
    )
  }
  if (snapshot.formatVersion !== POLICY_SYNC_FORMAT_VERSION) {
    throw new ConfigurationDomainError(
      'UNSUPPORTED_BASELINE_VERSION',
      `Unsupported Standard Revision format ${String(snapshot.formatVersion)}`,
    )
  }

  const actualHash = await hashCanonicalValue(snapshot)
  const parsed = v.safeParse(policySyncInputSchema, snapshot)
  if (!parsed.success) {
    throw new ConfigurationDomainError(
      'BASELINE_INCOMPLETE',
      'Standard Revision snapshot is incomplete',
      {
        issues: parsed.issues,
        [REVISION_ISSUES_KEY]: uniqueIssues([
          ...parsed.issues.map((issue) => schemaIssue(issue, snapshot)),
          ...indicationIssues(snapshot),
        ]),
      },
    )
  }
  const input = parsed.output

  if (input.drug.status !== 'finalized') {
    throw new ConfigurationDomainError(
      'BASELINE_MUTABLE',
      'A Standard Revision must come from finalized Drug Builder state',
    )
  }
  if (
    !input.drug.setId.trim() ||
    input.revision <= 0 ||
    !ref.id.trim() ||
    ref.revisionNumber <= 0 ||
    !ref.snapshotKey.trim() ||
    !ref.snapshotHash.trim() ||
    ref.drugId !== input.drug.setId
  ) {
    throw new ConfigurationDomainError(
      'BASELINE_INCOMPLETE',
      'Standard Revision identity does not match its snapshot',
    )
  }
  if (
    input.applicableTypes.some(
      (type) => !supportedTypes.has(type as PolicyIndicationEditType),
    )
  ) {
    throw new ConfigurationDomainError(
      'BASELINE_INCOMPLETE',
      'Standard Revision contains an unsupported policy type',
    )
  }

  const issues = indicationIssues(input)
  const first = issues.find(({ kind }) => kind === 'duplicate') ?? issues[0]
  if (first) {
    throw new ConfigurationDomainError(
      first.kind === 'duplicate'
        ? 'DUPLICATE_INDICATION_ID'
        : 'BASELINE_INCOMPLETE',
      first.message,
      { indicationId: first.indicationId, [REVISION_ISSUES_KEY]: issues },
    )
  }
  const sourceVersions = sourceVersionsFrom(input)
  if (actualHash !== ref.snapshotHash) {
    throw new ConfigurationDomainError(
      'BASELINE_HASH_MISMATCH',
      'Standard Revision snapshot hash does not match its reference',
      { expected: ref.snapshotHash, actual: actualHash },
    )
  }

  return canonicalClone({
    ref: {
      ...ref,
      sourcePolicySyncRevision: input.revision,
      sourceVersions,
    },
    drug: {
      setId: input.drug.setId,
      brandName: input.drug.brandName,
      genericName: input.drug.genericName,
      rialticDrugName: input.drug.rialticDrugName,
      description: input.drug.description,
      formulation: input.drug.formulation,
    },
    indications: [...input.completedIndications].sort((left, right) =>
      compareCodeUnits(left.id, right.id),
    ),
    applicableTypes: [...new Set(input.applicableTypes)].sort(compareCodeUnits),
  })
}
